import { Outlet, useNavigate } from 'react-router';
import {
  LayoutDashboard,
  Users,
  UserCheck,
  UserPlus,
  Receipt,
  Upload,
  Building2,
  FileClock,
  LogOut,
  User as UserIcon,
  ShieldCheck,
} from 'lucide-react';
import {
  UniversalAppLayout,
  UniversalTopNav,
  UniversalAppFooter,
  UserMenu,
  ThemeToggle,
  type TopNavItem,
  BrandLogo,
} from '@sighthost/ui';
import { siteConfig } from '@sighthost/config';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useStaffRole } from '@/shared/hooks/useStaffRole';
import { isInternalUser } from '@/shared/components/routing/defaultRouteForUser';

/**
 * SalesLayoutWrapper — chrome for the /sales/* surface.
 *
 * Per SP-9 (separate /sales surface to keep multi-tenancy clean) and the
 * three-layer isolation model (route guard + nav-gate + RLS). External sales
 * staff never see /admin/* tabs in this nav.
 *
 * Nav per role (per v0.2 §10.1):
 *  - sales_agent → Dashboard | My Leads | My Customers | My Commission
 *  - sales_manager → Dashboard | Leads | Agents | Customers | Commission | Import
 *  - admin (on /sales/*) → above + Companies
 */

const SALES_THEME_ACCENT = '#7C4A8C'; // distinct from admin's teal — visually flags "different surface"

export function SalesLayoutWrapper() {
  const { user, signOut } = useAuth();
  const { staffRole } = useStaffRole();
  const navigate = useNavigate();

  const userName = user?.email?.split('@')[0] ?? 'Staff';
  const userEmail = user?.email;

  const items = navForRole(staffRole);

  async function signOutToMarketing() {
    try {
      await signOut();
    } catch {
      /* no-op */
    }
    window.location.href = siteConfig.url;
  }

  return (
    <UniversalAppLayout
      topNav={
        <UniversalTopNav
          accentColor={SALES_THEME_ACCENT}
          brand={
            <span className="flex items-center gap-3">
              <BrandLogo tone="white" className="h-10" loading="eager" />
              <span className="hidden font-mono text-[9px] uppercase tracking-[0.3em] text-white/50 sm:block">
                Sales Console
              </span>
            </span>
          }
          items={items}
          right={
            <>
            {/* Same control as the admin and sales shells — one component, one
                store, one storage key. Hidden below `sm`: the mobile bar has no
                room, and `system` already tracks the phone own setting. */}
            <ThemeToggle onAccent className="mr-1 hidden sm:inline-flex" />
            <UserMenu
              name={userName}
              email={userEmail}
              items={[
                // Show the switch to any user who actually has admin-console
                // access: a Phase-11 admin (staff_profile.role='admin') OR an
                // internal auth user (staff/support). A support agent who is
                // also a sales_agent (e.g. Orapeleng) needs this to get back.
                // Pure external sales agents have neither → never see it.
                ...(staffRole === 'admin' || isInternalUser(user) ? [{
                  label: 'Switch to Admin Console',
                  icon: ShieldCheck,
                  onSelect: () => { window.location.href = '/admin'; },
                }] : []),
                {
                  label: 'Profile settings',
                  icon: UserIcon,
                  onSelect: () => navigate('/sales/profile'),
                },
                {
                  label: 'Sign out',
                  icon: LogOut,
                  tone: 'danger',
                  separated: true,
                  onSelect: signOutToMarketing,
                },
              ]}
            />
            </>
          }
        />
      }
      footer={
        <UniversalAppFooter
          legalName={siteConfig.legalName}
          registration={siteConfig.registration}
        />
      }
    >
      <Outlet />
    </UniversalAppLayout>
  );
}

function navForRole(role: string | null): TopNavItem[] {
  if (role === 'sales_agent') {
    return [
      { label: 'Dashboard', to: '/sales', end: true, icon: LayoutDashboard },
      { label: 'Add Customer', to: '/sales/customers/new', icon: UserPlus },
      { label: 'My Leads', to: '/sales/my-leads', icon: Users },
      { label: 'Quotations', to: '/sales/quotations', icon: FileClock },
      { label: 'My Customers', to: '/sales/customers', icon: UserCheck },
      { label: 'Commission', to: '/sales/commission', icon: Receipt },
    ];
  }
  // sales_manager + admin see the manager set; admin adds Companies
  const base: TopNavItem[] = [
    { label: 'Dashboard', to: '/sales', end: true, icon: LayoutDashboard },
    { label: 'Add Customer', to: '/sales/customers/new', icon: UserPlus },
    { label: 'Leads', to: '/sales/leads', icon: Users },
    { label: 'Quotations', to: '/sales/quotations', icon: FileClock },
    { label: 'Agents', to: '/sales/agents', icon: UserPlus },
    { label: 'Customers', to: '/sales/customers', icon: UserCheck },
    { label: 'Commission', to: '/sales/commission', icon: Receipt },
    { label: 'Import', to: '/sales/import', icon: Upload },
  ];
  if (role === 'admin') {
    // Index 6 = immediately after Customers, before Commission. Recomputed when
    // Quotations was inserted at index 3 (2026-07-27) — the previous literal 5
    // would now land before Customers and silently reorder the nav.
    base.splice(6, 0, { label: 'Companies', to: '/sales/companies', icon: Building2 });
  }
  return base;
}
